/**
 * Périmètre d'un rapport avancé.
 *
 * Un seul objet, construit une fois et repris partout : les réglages de
 * l'assistant (« Affiner »), la sélection emportée depuis l'exploration, et
 * chacun des groupes d'une comparaison. Ses filtres portent les noms de
 * l'exploration (`prixMin`, `pays`, `search`…) : une sélection passe de l'une
 * à l'autre sans traduction.
 *
 * Fonctions pures : l'API s'en sert pour valider une sélection et écrire la
 * ligne de périmètre des rapports, le front pour ses puces et ses aperçus.
 */

import type { FamilleArtisanat } from '../enums/index.js';
import {
  FAMILLE_LABELS,
  PAYS_BOUTIQUE,
  PERIODES_VENTES,
  PERIODE_VENTES_LABELS,
} from '../constants/index.js';
import { libelleCategorie, libelleLangue, libelleOrigine } from '../format/index.js';

// ── Formes ─────────────────────────────────────────────────────────────────

/** Réglages d'un périmètre. Tout est facultatif : un réglage absent ne filtre rien. */
export interface FiltresPerimetre {
  familles?: FamilleArtisanat[];
  /** Chemins de taxonomie exacts. */
  categories?: string[];
  /** Pays des boutiques retenus (ISO 3166-1 alpha-2). */
  pays?: string[];
  /** Pays des boutiques écartés ; une boutique sans pays déclaré est écartée aussi. */
  horsPays?: string[];
  /** Origines de découverte : mot-clé déclencheur, ou `via_avis`. */
  origines?: string[];
  /** Boutiques désignées (uuid). */
  boutiques?: string[];
  /** Bornes du prix comparable, en euros : seuls les prix convertis se comparent. */
  prixMin?: number;
  prixMax?: number;
  avecVariantes?: boolean;
  avecVentes?: boolean;
  noteMin?: number;
  /** Terme cherché dans le titre, la boutique et les tags, comme dans l'exploration. */
  search?: string;
  /** Langues des acheteurs : elles filtrent les ventes et les avis lus, pas les articles suivis. */
  langues?: string[];
}

/** Périodes d'un rapport : celles de la page Ventes, ou des mois choisis. */
export const PERIODES_RAPPORT = [...PERIODES_VENTES, 'dates'] as const;
export type PeriodeRapportCle = (typeof PERIODES_RAPPORT)[number];

export interface PeriodeRapport {
  periode: PeriodeRapportCle;
  /** Premier mois inclus (AAAA-MM), pour `dates` seulement. */
  du?: string;
  /** Dernier mois inclus (AAAA-MM), pour `dates` seulement. */
  au?: string;
  /** Compare à la même période un an plus tôt. Sans effet sur le cumul. */
  comparer: boolean;
}

/**
 * Douze mois comparés aux douze précédents. Sans période, un rapport compte
 * les ventes depuis le début du suivi : une note trimestrielle y citait
 * treize ans de ventes.
 */
export const PERIODE_RAPPORT_DEFAUT: Readonly<PeriodeRapport> = { periode: '12m', comparer: true };

/** Découpage d'un périmètre emporté depuis l'exploration : une unité par valeur. */
export const DECOUPAGES = ['aucun', 'boutique', 'gamme', 'pays', 'famille', 'terme'] as const;
export type Decoupage = (typeof DECOUPAGES)[number];

export const DECOUPAGE_LABELS: Record<Decoupage, string> = {
  aucun: 'Un seul bloc',
  boutique: 'Par boutique',
  gamme: 'Par gamme de prix',
  pays: 'Par pays',
  famille: 'Par famille',
  terme: 'Par terme',
};

/** Unités d'un découpage, « Autres » non compris : au-delà, chaque section deviendrait une liste. */
export const UNITES_DECOUPAGE_MAX = 5;

/** Un groupe d'une comparaison : un nom et ses réglages. */
export interface GroupePerimetre {
  nom: string;
  filtres: FiltresPerimetre;
}

export const GROUPES_MIN = 2;
export const GROUPES_MAX = 4;
export const NOM_GROUPE_MAX = 80;

export interface SegmentPerimetre {
  famille: FamilleArtisanat;
  categorie: string;
}

/**
 * Sélection d'un rapport, telle que stockée dans `RapportAvance.selection`.
 *
 * - familles et segments : les unités de l'assistant, affinées par `filtres` ;
 * - sans famille ni segment, `filtres` est la sélection de l'exploration,
 *   éventuellement découpée ;
 * - `groupes` : deux à quatre périmètres comparés, sur la même période.
 *
 * Sans `periode`, les ventes se cumulent depuis le début du suivi : c'est le
 * comportement des rapports antérieurs, qu'une relance recalcule à l'identique.
 */
export interface SelectionPerimetre {
  familles: FamilleArtisanat[];
  segments: SegmentPerimetre[];
  filtres?: FiltresPerimetre;
  periode?: PeriodeRapport;
  decoupage?: Decoupage;
  groupes?: GroupePerimetre[];
}

export type ModeSelection = 'segments' | 'exploration' | 'comparaison';

export function modeSelection(selection: SelectionPerimetre): ModeSelection {
  if (selection.groupes && selection.groupes.length > 0) return 'comparaison';
  if (selection.familles.length + selection.segments.length === 0 && selection.filtres) {
    return 'exploration';
  }
  return 'segments';
}

/** Mesures d'un périmètre sur la période, calculées par le code. */
export interface IndicateursPerimetre {
  articles: number;
  boutiques: number;
  ventes: number;
  /** Ventes de la même période un an plus tôt ; null sans comparaison. */
  ventesPrecedentes: number | null;
  /** Prix comparable médian des articles suivis, en euros. */
  prixMedian: number | null;
  /** Note moyenne des avis de la période. */
  note: number | null;
  /** Part des ventes de la période passées par des acheteurs anglophones (0 à 1). */
  partAnglophone: number | null;
}

// ── Nettoyage ──────────────────────────────────────────────────────────────

const LISTES = [
  'familles',
  'categories',
  'pays',
  'horsPays',
  'origines',
  'boutiques',
  'langues',
] as const;

/**
 * Réglages sans listes vides, sans doublons, sans terme blanc : ce qui reste
 * filtre vraiment. Deux sélections équivalentes s'écrivent alors pareil.
 */
export function nettoyerFiltres(filtres: FiltresPerimetre | undefined): FiltresPerimetre {
  const propre: FiltresPerimetre = {};
  if (!filtres) return propre;
  for (const cle of LISTES) {
    const valeurs = filtres[cle]?.filter((v) => typeof v === 'string' && v.trim() !== '');
    if (valeurs && valeurs.length > 0) {
      (propre as Record<string, unknown>)[cle] = [...new Set(valeurs)];
    }
  }
  for (const cle of ['prixMin', 'prixMax', 'noteMin'] as const) {
    const valeur = filtres[cle];
    if (typeof valeur === 'number' && Number.isFinite(valeur)) propre[cle] = valeur;
  }
  for (const cle of ['avecVariantes', 'avecVentes'] as const) {
    const valeur = filtres[cle];
    if (typeof valeur === 'boolean') propre[cle] = valeur;
  }
  const terme = filtres.search?.trim();
  if (terme) propre.search = terme;
  return propre;
}

/** Vrai quand aucun réglage ne filtre : le périmètre couvrirait tout le corpus. */
export function filtresVides(filtres: FiltresPerimetre | undefined): boolean {
  return Object.keys(nettoyerFiltres(filtres)).length === 0;
}

/**
 * Sélection réduite à ce qui compte, telle qu'elle sera stockée : réglages
 * nettoyés, dates seulement pour une période choisie, découpage seulement
 * pour une sélection de l'exploration, noms de groupes sans espaces.
 */
export function normaliserSelection(selection: SelectionPerimetre): SelectionPerimetre {
  const { periode } = selection;
  const propre: SelectionPerimetre = {
    familles: [...new Set(selection.familles)],
    segments: selection.segments.map((s) => ({ famille: s.famille, categorie: s.categorie })),
    ...(selection.filtres ? { filtres: nettoyerFiltres(selection.filtres) } : {}),
    ...(periode
      ? {
          periode:
            periode.periode === 'dates'
              ? { periode: 'dates', du: periode.du, au: periode.au, comparer: periode.comparer }
              : { periode: periode.periode, comparer: periode.comparer },
        }
      : {}),
    ...(selection.groupes?.length
      ? {
          groupes: selection.groupes.map((g) => ({
            nom: g.nom.trim(),
            filtres: nettoyerFiltres(g.filtres),
          })),
        }
      : {}),
  };
  if (selection.decoupage && modeSelection(propre) === 'exploration') {
    propre.decoupage = selection.decoupage;
  }
  return propre;
}

// ── Validation ─────────────────────────────────────────────────────────────

const MOIS = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Mois courant au format AAAA-MM (UTC). */
function moisCourant(maintenant: Date): string {
  return `${maintenant.getUTCFullYear()}-${String(maintenant.getUTCMonth() + 1).padStart(2, '0')}`;
}

function erreurFiltres(filtres: FiltresPerimetre | undefined): string | null {
  if (
    filtres?.prixMin !== undefined &&
    filtres.prixMax !== undefined &&
    filtres.prixMin > filtres.prixMax
  ) {
    return 'Le prix minimum dépasse le prix maximum.';
  }
  return null;
}

function erreurPeriode(periode: PeriodeRapport | undefined, maintenant: Date): string | null {
  if (!periode || periode.periode !== 'dates') return null;
  const { du, au } = periode;
  if (!du || !au || !MOIS.test(du) || !MOIS.test(au)) {
    return 'Choisissez le premier et le dernier mois de la période.';
  }
  if (du > au) return 'Le premier mois de la période suit le dernier.';
  if (au > moisCourant(maintenant)) return 'La période ne peut pas finir dans le futur.';
  return null;
}

/**
 * Message d'erreur d'une sélection, ou null si elle peut produire un rapport.
 * L'API la refuse en 400 avec ce message ; l'assistant s'en sert pour griser
 * le bouton « Suivant ».
 */
export function erreurSelection(
  selection: SelectionPerimetre,
  maintenant: Date = new Date(),
): string | null {
  const periode = erreurPeriode(selection.periode, maintenant);
  if (periode) return periode;

  switch (modeSelection(selection)) {
    case 'comparaison': {
      const groupes = selection.groupes ?? [];
      if (groupes.length < GROUPES_MIN) return 'Comparez au moins deux périmètres.';
      if (groupes.length > GROUPES_MAX) return `Comparez ${GROUPES_MAX} périmètres au plus.`;
      const noms = new Set<string>();
      for (const groupe of groupes) {
        const nom = groupe.nom.trim();
        if (!nom) return 'Nommez chaque périmètre.';
        const cle = nom.toLocaleLowerCase('fr');
        if (noms.has(cle)) return `Deux périmètres s'appellent « ${nom} ».`;
        noms.add(cle);
        if (filtresVides(groupe.filtres)) {
          return `Le périmètre « ${nom} » n'a aucun réglage : il couvrirait tout le corpus.`;
        }
        const filtres = erreurFiltres(groupe.filtres);
        if (filtres) return `${nom} : ${filtres}`;
      }
      return null;
    }
    case 'exploration':
      if (filtresVides(selection.filtres)) {
        return "La sélection est vide : posez au moins un filtre dans l'exploration.";
      }
      return erreurFiltres(selection.filtres);
    case 'segments':
      if (selection.familles.length + selection.segments.length === 0) {
        return 'La sélection est vide : choisissez au moins une famille ou un segment.';
      }
      return erreurFiltres(selection.filtres);
  }
}

// ── Nombres ────────────────────────────────────────────────────────────────
// Espaces et signes simples : ces libellés partent aussi dans les exports PDF,
// dont les polices standard ne couvrent ni l'espace fine ni le signe moins.

/** 17344 → « 17 344 ». */
export function entierFr(valeur: number): string {
  const arrondi = Math.round(valeur);
  const groupe = String(Math.abs(arrondi)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return arrondi < 0 ? `-${groupe}` : groupe;
}

/** 0.2834 → « 0,28 ». */
export function decimalFr(valeur: number, chiffres = 1): string {
  return valeur.toFixed(chiffres).replace('.', ',');
}

/** Évolution relative, 0.046 → « +4,6 % ». */
export function libelleEvolution(evolution: number): string {
  const pourcent = Math.round(evolution * 1000) / 10;
  return `${pourcent > 0 ? '+' : ''}${decimalFr(pourcent, 1)} %`;
}

/** Évolution d'une mesure sur un an ; null sans base de comparaison. */
export function evolution(courant: number, precedent: number | null): number | null {
  if (precedent === null || precedent <= 0) return null;
  return (courant - precedent) / precedent;
}

// ── Libellés ───────────────────────────────────────────────────────────────

/**
 * « en » → « Anglophones » : la langue de l'avis, lue comme l'acheteur. Même
 * libellé que l'interface (`libelleLangue`), gardé sous ce nom pour les rapports.
 */
export function libelleAcheteurs(code: string): string {
  return libelleLangue(code);
}

const MOIS_COURTS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

/** « 2026-06 » → « juin 2026 ». */
export function libelleMois(mois: string): string {
  const [annee, rang] = mois.split('-').map(Number);
  return MOIS_COURTS[rang - 1] ? `${MOIS_COURTS[rang - 1]} ${annee}` : mois;
}

const LIBELLES_PAYS = new Map(PAYS_BOUTIQUE.map((p) => [p.code, p.libelle]));
const libellePays = (code: string) => LIBELLES_PAYS.get(code) ?? code;

function listeLisible(elements: string[]): string {
  if (elements.length <= 1) return elements.join('');
  return `${elements.slice(0, -1).join(', ')} et ${elements.at(-1)}`;
}

/** Bornes de prix : « 175 – 671 € », « 300 € et plus », « moins de 175 € ». */
export function libellePrix(prixMin: number | undefined, prixMax: number | undefined): string {
  if (prixMin !== undefined && prixMax !== undefined) {
    return `${entierFr(prixMin)} – ${entierFr(prixMax)} €`;
  }
  if (prixMin !== undefined) return `${entierFr(prixMin)} € et plus`;
  if (prixMax !== undefined) return `moins de ${entierFr(prixMax)} €`;
  return 'Tous les prix';
}

/** Origine des boutiques : « Boutiques marocaines », « Boutiques hors Maroc »… */
export function libelleBoutiquesPays(filtres: FiltresPerimetre): string | null {
  const retenus = filtres.pays ?? [];
  const exclus = filtres.horsPays ?? [];
  const parties: string[] = [];
  if (retenus.length === 1 && retenus[0] === 'MA') parties.push('Boutiques marocaines');
  else if (retenus.length > 0)
    parties.push(`Boutiques : ${listeLisible(retenus.map(libellePays))}`);
  if (exclus.length > 0) parties.push(`Boutiques hors ${listeLisible(exclus.map(libellePays))}`);
  return parties.length > 0 ? parties.join(', ') : null;
}

/**
 * Puces lisibles des réglages posés, dans un ordre stable. Les boutiques
 * désignées sont nommées si leurs noms sont fournis, comptées sinon.
 */
export function pucesFiltres(
  filtres: FiltresPerimetre | undefined,
  nomsBoutiques: Readonly<Record<string, string>> = {},
): string[] {
  const f = nettoyerFiltres(filtres);
  const puces: string[] = [];
  for (const famille of f.familles ?? []) puces.push(FAMILLE_LABELS[famille] ?? famille);
  for (const categorie of f.categories ?? []) puces.push(libelleCategorie(categorie, 1));
  const pays = libelleBoutiquesPays(f);
  if (pays) puces.push(pays);
  for (const origine of f.origines ?? []) puces.push(libelleOrigine(origine));
  if (f.boutiques) {
    const noms = f.boutiques.map((id) => nomsBoutiques[id]).filter(Boolean);
    puces.push(
      noms.length === f.boutiques.length && noms.length <= 3
        ? listeLisible(noms)
        : `${f.boutiques.length} boutique${f.boutiques.length > 1 ? 's' : ''} désignée${f.boutiques.length > 1 ? 's' : ''}`,
    );
  }
  if (f.prixMin !== undefined || f.prixMax !== undefined) {
    puces.push(libellePrix(f.prixMin, f.prixMax));
  }
  if (f.noteMin !== undefined) puces.push(`Note ${decimalFr(f.noteMin, 1)} et plus`);
  if (f.avecVariantes !== undefined) {
    puces.push(f.avecVariantes ? 'Avec variantes' : 'Sans variantes');
  }
  if (f.avecVentes !== undefined) {
    puces.push(f.avecVentes ? 'Avec ventes mesurées' : 'Sans vente mesurée');
  }
  if (f.search) puces.push(`« ${f.search} »`);
  if (f.langues) {
    puces.push(
      `Acheteurs ${listeLisible(f.langues.map((l) => libelleAcheteurs(l).toLowerCase()))}`,
    );
  }
  return puces;
}

/** Libellé court d'une période, pour une puce : « 12 mois vs N-1 ». */
export function libellePeriodeCourte(
  periode: PeriodeRapport | undefined,
  maintenant: Date = new Date(),
): string {
  if (!periode) return 'Depuis le début';
  let base: string;
  if (periode.periode === 'dates') {
    base =
      periode.du && periode.au
        ? periode.du === periode.au
          ? libelleMois(periode.du)
          : `${libelleMois(periode.du)} – ${libelleMois(periode.au)}`
        : 'Dates à choisir';
  } else if (periode.periode === 'annee') {
    base = `Année ${maintenant.getUTCFullYear()}`;
  } else {
    base = PERIODE_VENTES_LABELS[periode.periode];
  }
  return periode.comparer && periode.periode !== 'tout' ? `${base} vs N-1` : base;
}

/** Libellé complet d'une période, pour la ligne de périmètre d'un rapport. */
export function libellePeriodeLongue(
  periode: PeriodeRapport | undefined,
  maintenant: Date = new Date(),
): string {
  if (!periode || periode.periode === 'tout') return 'Ventes cumulées depuis le début du suivi';
  let base: string;
  switch (periode.periode) {
    case '90j':
      base = 'Ventes des 90 derniers jours';
      break;
    case '12m':
      base = 'Ventes des 12 derniers mois';
      break;
    case 'annee':
      base = `Ventes de l'année ${maintenant.getUTCFullYear()} à ce jour`;
      break;
    default:
      base =
        periode.du && periode.au && periode.du !== periode.au
          ? `Ventes de ${libelleMois(periode.du)} à ${libelleMois(periode.au)}`
          : `Ventes de ${libelleMois(periode.du ?? periode.au ?? '')}`;
  }
  return periode.comparer ? `${base}, comparées à la même période un an plus tôt` : base;
}

/** « Tapis », « Tapis, Poterie et céramique — Vases et 3 autres segments ». */
export function libelleUnitesSegments(
  familles: readonly FamilleArtisanat[],
  segments: readonly SegmentPerimetre[],
): string {
  const parties = [
    ...familles.map((f) => FAMILLE_LABELS[f] ?? f),
    ...segments.map(
      (s) => `${FAMILLE_LABELS[s.famille] ?? s.famille} — ${libelleCategorie(s.categorie, 1)}`,
    ),
  ];
  if (parties.length <= 3) return parties.join(', ');
  return `${parties.slice(0, 2).join(', ')} et ${parties.length - 2} autres segments`;
}

/**
 * Ligne de périmètre reprise sous le titre du rapport, à l'écran comme en PDF
 * et en Word : ce que couvre le rapport, en une phrase.
 */
export function lignePerimetre(
  selection: SelectionPerimetre,
  options: { maintenant?: Date; nomsBoutiques?: Readonly<Record<string, string>> } = {},
): string {
  const mode = modeSelection(selection);
  const parties: string[] = [];
  if (mode === 'comparaison') {
    const noms = (selection.groupes ?? []).map((g) => g.nom.trim());
    parties.push(`Comparaison de ${noms.length} périmètres : ${noms.join(', ')}`);
  } else {
    if (mode === 'segments') {
      parties.push(libelleUnitesSegments(selection.familles, selection.segments));
    }
    const puces = pucesFiltres(selection.filtres, options.nomsBoutiques);
    if (mode === 'exploration' && puces.length === 0) parties.push('Tout le corpus');
    parties.push(...puces);
    if (mode === 'exploration' && selection.decoupage && selection.decoupage !== 'aucun') {
      parties.push(`découpé ${DECOUPAGE_LABELS[selection.decoupage].toLowerCase()}`);
    }
  }
  parties.push(libellePeriodeLongue(selection.periode, options.maintenant));
  return parties.filter(Boolean).join(' · ');
}

// ── Comparaison ────────────────────────────────────────────────────────────

export interface LigneComparaison {
  libelle: string;
  /** Une valeur formatée par périmètre, dans l'ordre des groupes. */
  valeurs: string[];
  /** Évolution sur un an, par périmètre : ligne des ventes seulement. */
  evolutions?: (number | null)[];
}

/**
 * Tableau comparatif des périmètres, ligne à ligne. Le même sert l'aperçu de
 * l'assistant et le contexte transmis à l'IA, qui le reprend tel quel.
 */
export function lignesComparaison(
  indicateurs: readonly IndicateursPerimetre[],
  libelleVentes = 'Ventes sur la période',
): LigneComparaison[] {
  const tiret = '—';
  const lignes: LigneComparaison[] = [
    { libelle: 'Articles suivis', valeurs: indicateurs.map((i) => entierFr(i.articles)) },
    { libelle: 'Boutiques', valeurs: indicateurs.map((i) => entierFr(i.boutiques)) },
    {
      libelle: 'Prix médian',
      valeurs: indicateurs.map((i) =>
        i.prixMedian === null ? tiret : `${entierFr(i.prixMedian)} €`,
      ),
    },
    {
      libelle: libelleVentes,
      valeurs: indicateurs.map((i) => entierFr(i.ventes)),
      evolutions: indicateurs.map((i) => evolution(i.ventes, i.ventesPrecedentes)),
    },
    {
      libelle: 'Ventes par article suivi',
      valeurs: indicateurs.map((i) =>
        i.articles > 0 ? decimalFr(i.ventes / i.articles, 2) : tiret,
      ),
    },
    {
      libelle: 'Note moyenne',
      valeurs: indicateurs.map((i) => (i.note === null ? tiret : decimalFr(i.note, 2))),
    },
    {
      libelle: 'Acheteurs anglophones',
      valeurs: indicateurs.map((i) =>
        i.partAnglophone === null ? tiret : `${Math.round(i.partAnglophone * 100)} %`,
      ),
    },
  ];
  // Sans comparaison demandée, aucune évolution : la colonne resterait vide.
  if (indicateurs.every((i) => i.ventesPrecedentes === null)) delete lignes[3].evolutions;
  return lignes;
}

// ── Nom proposé ────────────────────────────────────────────────────────────

/**
 * Bornes du nom proposé à la création d'un rapport : assez long pour dire ce
 * qu'il couvre, assez court pour se lire d'un coup d'œil dans une liste.
 */
export const NOM_PROPOSE_MIN = 28;
export const NOM_PROPOSE_MAX = 72;

export interface ContexteNomRapport {
  /** Nom du modèle choisi : il complète un nom trop court. */
  modele?: string | null;
  /** Quartiles des prix des unités : des bornes qui les suivent se lisent en gammes. */
  quartiles?: { p25: number | null; p75: number | null } | null;
  nomsBoutiques?: Readonly<Record<string, string>>;
  maintenant?: Date;
}

/** Un réglage qui distingue le périmètre, et sa résistance au manque de place. */
interface Qualificatif {
  texte: string;
  /** Les plus faibles tombent d'abord quand le nom déborde. */
  poids: number;
}

/** Deux libellés qui contiennent déjà « et » se séparent d'une barre oblique. */
function reunir(libelles: string[]): string {
  const uniques = [...new Set(libelles)];
  return uniques.some((l) => l.includes(' et ')) ? uniques.join(' / ') : listeLisible(uniques);
}

/** Sujets possibles d'un rapport de l'assistant, du plus précis au plus court. */
function sujetsSegments(
  familles: readonly FamilleArtisanat[],
  segments: readonly SegmentPerimetre[],
): string[] {
  const unites = [
    ...familles.map((f) => FAMILLE_LABELS[f] ?? f),
    ...segments.map((s) => libelleCategorie(s.categorie, 1)),
  ];
  const distinctes = [...new Set([...familles, ...segments.map((s) => s.famille)])];
  const nomsFamilles = distinctes.map((f) => FAMILLE_LABELS[f] ?? f);
  if (unites.length === 0) return [];
  if (unites.length <= 2) {
    return [reunir(unites), ...(segments.length > 0 ? [reunir(nomsFamilles)] : [])];
  }
  if (segments.length === 0) return [`${unites.length} familles d'artisanat`];
  if (distinctes.length <= 2) {
    return [`${reunir(nomsFamilles)} (${unites.length} segments)`, reunir(nomsFamilles)];
  }
  return [`${unites.length} segments de ${distinctes.length} familles`];
}

/** Sujets possibles d'une sélection de l'exploration : catégories, sinon familles. */
function sujetsFiltres(filtres: FiltresPerimetre): string[] {
  const familles = (filtres.familles ?? []).map((f) => FAMILLE_LABELS[f] ?? f);
  const categories = [...new Set((filtres.categories ?? []).map((c) => libelleCategorie(c, 1)))];
  const sujets: string[] = [];
  if (categories.length > 0 && categories.length <= 2) sujets.push(reunir(categories));
  if (familles.length > 0 && familles.length <= 2) sujets.push(reunir(familles));
  else if (familles.length > 2) sujets.push(`${familles.length} familles d'artisanat`);
  if (sujets.length === 0) {
    sujets.push(categories.length > 2 ? `${categories.length} catégories` : 'Artisanat');
  }
  return sujets;
}

/** Sujets possibles d'une comparaison : les noms des groupes, sinon leur nombre. */
function sujetsComparaison(groupes: readonly GroupePerimetre[]): string[] {
  const noms = groupes.map((g) => g.nom.trim()).filter(Boolean);
  const familles = groupes.map((g) => g.filtres.familles ?? []);
  const premiere = familles[0]?.[0];
  // Une famille commune à tous les groupes situe la comparaison, si aucun nom ne la dit déjà.
  const commune =
    premiere && familles.every((l) => l.length === 1 && l[0] === premiere)
      ? (FAMILLE_LABELS[premiere] ?? premiere)
      : null;
  const dejaDite = commune
    ? noms.some((n) => n.toLocaleLowerCase('fr').includes(commune.toLocaleLowerCase('fr')))
    : true;
  const situer = (sujet: string) => (dejaDite ? sujet : `${commune} : ${sujet}`);
  const sujets: string[] = [];
  if (noms.length === 2) sujets.push(`${noms[0]} face à ${noms[1]}`);
  else if (noms.length > 2) sujets.push(listeLisible(noms));
  sujets.push(`Comparaison de ${groupes.length} périmètres`);
  return sujets.map(situer);
}

/** Bornes de prix lues en gamme quand elles suivent les quartiles de la sélection. */
function libelleGamme(
  filtres: FiltresPerimetre,
  quartiles: ContexteNomRapport['quartiles'],
): string {
  const p25 = quartiles?.p25 ?? null;
  const p75 = quartiles?.p75 ?? null;
  const { prixMin, prixMax } = filtres;
  if (p25 !== null && p75 !== null) {
    if (prixMin === undefined && prixMax === p25) return 'entrée de gamme';
    if (prixMin === p25 && prixMax === p75) return 'cœur de gamme';
    if (prixMin === p75 && prixMax === undefined) return 'haut de gamme';
  }
  return libellePrix(prixMin, prixMax);
}

/**
 * Réglages qui distinguent le périmètre, dans l'ordre de lecture. Le découpage
 * résiste le mieux, puis l'origine des boutiques, le prix, enfin les langues.
 */
function qualificatifs(
  filtres: FiltresPerimetre,
  contexte: ContexteNomRapport,
  decoupage: Decoupage | undefined,
): Qualificatif[] {
  const q: Qualificatif[] = [];
  const retenus = filtres.pays ?? [];
  const exclus = filtres.horsPays ?? [];
  if (retenus.length === 1 && retenus[0] === 'MA' && exclus.length === 0) {
    q.push({ texte: 'boutiques marocaines', poids: 3 });
  } else if (exclus.length === 1 && retenus.length === 0) {
    q.push({ texte: `boutiques hors ${libellePays(exclus[0])}`, poids: 3 });
  } else if (retenus.length === 1) {
    q.push({ texte: `boutiques (${libellePays(retenus[0])})`, poids: 3 });
  } else if (retenus.length > 1) {
    q.push({ texte: `boutiques de ${retenus.length} pays`, poids: 3 });
  }
  if (filtres.boutiques?.length) {
    const n = filtres.boutiques.length;
    const nom = n === 1 ? contexte.nomsBoutiques?.[filtres.boutiques[0]] : undefined;
    q.push({ texte: nom ? `boutique ${nom}` : `${n} boutique${n > 1 ? 's' : ''}`, poids: 3 });
  }
  if (filtres.prixMin !== undefined || filtres.prixMax !== undefined) {
    q.push({ texte: libelleGamme(filtres, contexte.quartiles), poids: 2 });
  }
  if (filtres.langues?.length) {
    q.push({
      texte:
        filtres.langues.length <= 2
          ? `acheteurs ${listeLisible(filtres.langues.map((l) => libelleAcheteurs(l).toLowerCase()))}`
          : `${filtres.langues.length} langues d'acheteurs`,
      poids: 1,
    });
  }
  if (decoupage && decoupage !== 'aucun') {
    q.push({ texte: DECOUPAGE_LABELS[decoupage].toLowerCase(), poids: 4 });
  }
  return q;
}

/** « 12 mois », « 2026 », « janv.–juin 2026 »… : la période, pour un nom. */
function periodeDuNom(periode: PeriodeRapport | undefined, maintenant: Date): string | null {
  if (!periode) return null;
  switch (periode.periode) {
    case '90j':
      return '90 jours';
    case '12m':
      return '12 mois';
    case 'annee':
      return String(maintenant.getUTCFullYear());
    case 'tout':
      return 'depuis le début';
    default: {
      const { du, au } = periode;
      if (!du || !au) return null;
      if (du === au) return libelleMois(du);
      const moisDu = MOIS_COURTS[Number(du.slice(5, 7)) - 1];
      return du.slice(0, 4) === au.slice(0, 4)
        ? `${moisDu}–${libelleMois(au)}`
        : `${libelleMois(du)} – ${libelleMois(au)}`;
    }
  }
}

/** Coupe au dernier mot qui tient, points de suspension compris. */
function couper(texte: string, max: number): string {
  if (texte.length <= max) return texte;
  const coupe = texte.slice(0, max - 1);
  const espace = coupe.lastIndexOf(' ');
  return `${(espace > max / 2 ? coupe.slice(0, espace) : coupe).trimEnd()}…`;
}

/**
 * Nom proposé pour un rapport, d'après ce qui a été choisi : le sujet (unités,
 * sélection de l'exploration ou périmètres comparés), les réglages qui le
 * distinguent, puis la période — « Tapis, boutiques marocaines, cœur de
 * gamme — 12 mois ». Il tient entre `NOM_PROPOSE_MIN` et `NOM_PROPOSE_MAX`
 * caractères : quand la place manque, les réglages les moins parlants tombent
 * d'abord ; un nom trop court est précédé du nom du modèle.
 */
export function nomRapportPropose(
  selection: SelectionPerimetre,
  contexte: ContexteNomRapport = {},
): string {
  const maintenant = contexte.maintenant ?? new Date();
  const mode = modeSelection(selection);
  const filtres = nettoyerFiltres(selection.filtres);

  let sujets: string[];
  let qualifs: Qualificatif[] = [];
  if (mode === 'comparaison') {
    sujets = sujetsComparaison(selection.groupes ?? []);
  } else {
    const bruts =
      mode === 'segments'
        ? sujetsSegments(selection.familles, selection.segments)
        : sujetsFiltres(filtres);
    // Le terme cherché fait partie du sujet : « Tapis « beni ourain » ».
    sujets = filtres.search ? bruts.map((s) => `${s} « ${filtres.search} »`) : bruts;
    qualifs = qualificatifs(
      filtres,
      contexte,
      mode === 'exploration' ? selection.decoupage : undefined,
    );
  }
  if (sujets.length === 0) return `Rapport du ${maintenant.toLocaleDateString('fr-FR')}`;

  const periode = periodeDuNom(selection.periode, maintenant);
  const suffixe = periode ? ` — ${periode}` : '';
  const composer = (sujet: string, retenus: readonly Qualificatif[]) => {
    const corps = [sujet, ...retenus.map((q) => q.texte)].join(', ');
    return `${corps.charAt(0).toUpperCase()}${corps.slice(1)}${suffixe}`;
  };
  const completer = (nom: string) => {
    const modele = contexte.modele?.trim();
    if (nom.length >= NOM_PROPOSE_MIN || !modele) return nom;
    const complet = `${modele} : ${nom}`;
    return complet.length <= NOM_PROPOSE_MAX ? complet : nom;
  };

  for (const sujet of sujets) {
    let retenus = [...qualifs];
    let nom = composer(sujet, retenus);
    while (nom.length > NOM_PROPOSE_MAX && retenus.length > 0) {
      // Le moins parlant tombe d'abord ; à égalité, le dernier lu.
      const faible = retenus.reduce((min, q) => (q.poids <= min.poids ? q : min));
      retenus = retenus.filter((q) => q !== faible);
      nom = composer(sujet, retenus);
    }
    if (nom.length <= NOM_PROPOSE_MAX) return completer(nom);
  }
  return couper(composer(sujets[sujets.length - 1], []), NOM_PROPOSE_MAX);
}
